import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { listAs2InboundMessages } from '../api/as2InboundMessages';
import { CopyButton } from '../components/CopyButton';
import { PageHeader } from '../components/FeatureStatus';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { useAsyncData } from '../hooks/useAsyncData';
import type { As2MessageListQuery, As2MessageStatus } from '../types/api';

// Transport-level view over AS2 gateway inbound receipt/MDN state, proxied at
// /as2/inbound-messages. Distinct from the existing business-level /inbound-messages route
// (DocumentsPage + InboundMessageDetailPage), which models parsed inbound documents and does
// carry payload. This page is protocol-record-only — no payload field exists on the backing
// DTO — labeled "Inbound Messages (Transport)" in nav/breadcrumbs to avoid confusion.

const MESSAGE_STATUSES: As2MessageStatus[] = [
  'REQUEST_RECEIVED', 'PAYLOAD_STORED', 'PAYLOAD_EXTRACTED', 'MESSAGE_CREATED', 'MIC_CALCULATED',
  'SEND_ATTEMPTED', 'RECEIVED', 'QUEUED', 'SENDING', 'SENT', 'WAITING_FOR_ASYNC_MDN', 'MDN_OVERDUE',
  'RETRY_PENDING', 'RETRYING', 'DEAD_LETTERED', 'MDN_RECEIVED', 'MDN_FAILED', 'AWAITING_MDN',
  'DELIVERED', 'FAILED', 'REJECTED',
];
const PAGE_SIZE = 20;

// Pre-applies filters carried in the incoming URL (e.g. from the AS2 dashboard's KPI tile
// links), read once on first render, so `to={{ pathname, search }}` links from the dashboard
// actually land on a pre-filtered list rather than only preserving workspace/auth params.
function initialFiltersFromSearch(search: string): As2MessageListQuery {
  const params = new URLSearchParams(search);
  return {
    page: 0,
    size: PAGE_SIZE,
    partnerCode: params.get('partnerCode') ?? '',
    status: (params.get('status') as As2MessageStatus | null) ?? '',
    correlationId: params.get('correlationId') ?? '',
    fromDate: params.get('fromDate') ?? '',
    toDate: params.get('toDate') ?? '',
  };
}

export function As2InboundMessagesPage() {
  const { search } = useLocation();
  const [filters, setFilters] = useState<As2MessageListQuery>(() => initialFiltersFromSearch(search));

  const { data, isLoading, error, refresh } = useAsyncData(
    () => listAs2InboundMessages(filters),
    [JSON.stringify(filters)],
    { debounceMs: 250 },
  );

  const messages = data?.data ?? [];
  const failedCount = messages.filter((message) => message.status === 'FAILED').length;
  const deadLetteredCount = messages.filter((message) => message.status === 'DEAD_LETTERED').length;
  const awaitingMdnCount = messages.filter((message) => message.status === 'AWAITING_MDN').length;

  function patchFilters(patch: Partial<As2MessageListQuery>) {
    setFilters((current) => ({ ...current, ...patch, page: 0 }));
  }

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow="AS2 Gateway"
        title="AS2 inbound messages (transport)"
        purpose="Inspect the AS2 gateway's transport-level record of inbound receipts: signature/decryption checks, MDN delivery, and callback status."
        status="PRODUCTION_READY"
        connected
        next="This is protocol-level state only — no message payload is available here. Use Messages > Inbound Documents for business-level content."
      />
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Inbound messages</h2>
            <p>Receipts tracked by the AS2 gateway, one row per inbound AS2 message.</p>
          </div>
          <div className="panel-actions">
            <button type="button" onClick={() => void refresh()}>
              Refresh
            </button>
          </div>
        </div>
        <div className="operations-card-grid">
          <article className="operations-card">
            <span className="operations-card-label">Visible failed</span>
            <strong>{failedCount}</strong>
            <p>Inbound messages in the current result set that failed processing.</p>
          </article>
          <article className="operations-card">
            <span className="operations-card-label">Visible dead-lettered</span>
            <strong>{deadLetteredCount}</strong>
            <p>Inbound messages that exhausted retries and were dead-lettered.</p>
          </article>
          <article className="operations-card">
            <span className="operations-card-label">Awaiting MDN</span>
            <strong>{awaitingMdnCount}</strong>
            <p>Received messages still awaiting MDN generation or delivery.</p>
          </article>
        </div>
        <div className="toolbar toolbar-grid">
          <input
            aria-label="Partner code"
            placeholder="Partner code"
            value={filters.partnerCode ?? ''}
            onChange={(event) => patchFilters({ partnerCode: event.target.value })}
          />
          <select
            aria-label="Status"
            value={filters.status ?? ''}
            onChange={(event) => patchFilters({ status: event.target.value as As2MessageStatus | '' })}
          >
            <option value="">All statuses</option>
            {MESSAGE_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <input
            aria-label="Correlation ID"
            placeholder="Correlation ID"
            value={filters.correlationId ?? ''}
            onChange={(event) => patchFilters({ correlationId: event.target.value })}
          />
          <input
            aria-label="From date"
            type="date"
            value={filters.fromDate ?? ''}
            onChange={(event) => patchFilters({ fromDate: event.target.value })}
          />
          <input
            aria-label="To date"
            type="date"
            value={filters.toDate ?? ''}
            onChange={(event) => patchFilters({ toDate: event.target.value })}
          />
          <button
            type="button"
            className="ghost-button"
            onClick={() => setFilters({ page: 0, size: PAGE_SIZE, partnerCode: '', status: '', correlationId: '', fromDate: '', toDate: '' })}
          >
            Reset filters
          </button>
        </div>
        {isLoading ? <LoadingState label="Loading AS2 inbound messages..." /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && !error && messages.length === 0 ? <EmptyState label="No AS2 inbound messages matched the current filters." /> : null}
        {!isLoading && !error && messages.length > 0 ? (
          <>
            <table className="data-table data-table-cozy">
              <thead>
                <tr>
                  <th>Document type</th>
                  <th>Message ID</th>
                  <th>Partner</th>
                  <th>Content type</th>
                  <th>Status</th>
                  <th>MDN mode</th>
                  <th>Received</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((message) => (
                  <tr key={message.id}>
                    <td>
                      <div className="table-primary">
                        <Link to={{ pathname: `/as2/inbound-messages/${message.id}`, search }}>
                          {message.documentType ?? message.as2MessageId ?? message.id}
                        </Link>
                        <span className="inline-copy">
                          <span className="mono" title={message.id}>{shortId(message.id)}</span>
                          <CopyButton value={message.id} label="Copy message ID" />
                        </span>
                      </div>
                    </td>
                    <td>
                      {message.as2MessageId ? (
                        <span className="inline-copy">
                          <span className="mono" title={message.as2MessageId}>{shortId(message.as2MessageId)}</span>
                          <CopyButton value={message.as2MessageId} label="Copy AS2 message ID" />
                        </span>
                      ) : (
                        'n/a'
                      )}
                    </td>
                    <td>{message.partnerCode ?? 'n/a'}</td>
                    <td>{message.contentType ?? 'n/a'}</td>
                    <td><StatusBadge value={message.status} /></td>
                    <td>{message.mdnMode ?? 'n/a'}</td>
                    <td>{message.receivedAt ? new Date(message.receivedAt).toLocaleString('en-GB') : 'n/a'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination-bar">
              <button
                type="button"
                onClick={() => setFilters((current) => ({ ...current, page: Math.max(0, (current.page ?? 0) - 1) }))}
                disabled={(filters.page ?? 0) <= 0}
              >
                Previous
              </button>
              <span>Page {(filters.page ?? 0) + 1}</span>
              <button
                type="button"
                onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 0) + 1 }))}
                disabled={((data?.page ?? 0) + 1) * (data?.pageSize ?? PAGE_SIZE) >= (data?.total ?? 0)}
              >
                Next
              </button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
}

function shortId(value: string): string {
  return value.length > 18 ? `${value.slice(0, 18)}...` : value;
}
